import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ListChecks } from 'lucide-react';
import Screen from '../../demo/Screen';
import ScoreRing from '../../mocks/ScoreRing';
import {
  AlertGlyph,
  DoctorGlyph,
  FlaskGlyph,
  ForkKnifeGlyph,
  LeafyGreensArt,
  OrangeSliceArt,
  WaterGlassArt,
} from '../../mocks/ProductGlyphs';
import { DEFAULT_RESULT, TIER_TONE } from './HealthScoreParts';
import { ProductChip } from './ProductDetail';
import { STATUS_LABELS } from '../../mocks/sampleReport';
import './Recommendations.css';

const SCREEN = 'product.recommendations';

/*
 * Patient A's next steps (synthetic): an iron-deficiency pattern, low vitamin D
 * and a raised ESR — the same report as the health-scores screen.
 */
const COLUMNS = [
  {
    key: 'tests',
    icon: FlaskGlyph,
    tone: 'blue',
    title: 'Additional tests',
    sub: 'Fill the gaps. Get the full picture.',
    rows: [
      {
        slug: 'ferritin',
        title: 'Ferritin',
        sub: 'Check iron storage',
        pill: 'Recommended',
        why: 'Ferritin shows how much iron your body has in store. It confirms whether low iron is behind your low haemoglobin.',
      },
      {
        slug: 'vitamin-d-repeat',
        title: 'Vitamin D (repeat)',
        sub: 'Track improvement',
        pill: 'In 8–12 weeks',
        why: 'Your vitamin D was low. A repeat test after a few weeks of sun and supplements shows whether it is coming back up.',
      },
      {
        slug: 'repeat-esr',
        title: 'Repeat ESR',
        sub: 'Recheck after iron therapy',
        pill: 'In 6–8 weeks',
        why: 'Anaemia can raise ESR on its own. If it settles once your iron is back up, no further tests are needed for it.',
      },
    ],
    action: { id: 'view-all-tests', label: 'View all suggested tests' },
    more: {
      title: 'Also suggested',
      items: [
        'Vitamin B12: low B12 can also cause anaemia, so it is checked alongside iron.',
        'Thyroid profile (TSH): tiredness and hair fall can come from the thyroid too.',
        'Peripheral smear: the shape of your red cells confirms the type of anaemia.',
      ],
    },
  },
  {
    key: 'diet',
    icon: ForkKnifeGlyph,
    tone: 'green',
    title: 'Diet & nutrition',
    sub: 'Support your body, day by day.',
    rows: [
      {
        slug: 'iron-rich-foods',
        art: LeafyGreensArt,
        title: 'Iron-rich foods',
        sub: 'Leafy greens, legumes, lean meat',
        why: 'Palak, methi, rajma, chana and lean meat help rebuild iron. A squeeze of lemon or some amla with the meal helps your body absorb it.',
      },
      {
        slug: 'vitamin-d-sources',
        art: OrangeSliceArt,
        title: 'Vitamin D sources',
        sub: 'Sunlight, fortified foods, fatty fish',
        why: '15–20 minutes of morning sun on your arms and face, a few days a week, helps your body make vitamin D. Eggs, fatty fish and fortified milk add a little more.',
      },
      {
        slug: 'stay-hydrated',
        art: WaterGlassArt,
        disc: true,
        title: 'Stay hydrated',
        sub: 'At least 2–3 L water daily',
        why: 'Water supports your energy and digestion while your diet changes. Keep tea and coffee an hour away from meals, as they block iron.',
      },
    ],
    action: { id: 'view-diet-plan', label: 'View detailed diet plan' },
    more: {
      title: 'A sample day',
      items: [
        'Breakfast: vegetable poha with peanuts and a squeeze of lemon.',
        'Lunch: dal, palak sabzi, two rotis and a salad.',
        'Snack: roasted chana and an orange.',
        'Dinner: rajma or paneer with rice, and a bowl of curd.',
      ],
    },
  },
  {
    key: 'specialist',
    icon: DoctorGlyph,
    tone: 'purple',
    title: 'Consult a specialist',
    sub: 'Get expert advice, when you need it.',
    rows: [
      {
        slug: 'general-physician',
        title: 'General Physician',
        sub: 'For overall evaluation',
        pill: '1–2 weeks',
        why: 'A physician can confirm why your iron is low and decide whether you need iron tablets. Take this report with you.',
      },
      {
        slug: 'dietitian',
        title: 'Dietitian',
        sub: 'For personalized diet plan',
        pill: '1–2 weeks',
        why: 'A dietitian turns these food tips into a meal plan that fits your routine and the food you like.',
      },
      {
        slug: 'gynaecologist',
        title: 'Gynaecologist (optional)',
        sub: 'For hormonal assessment',
        pill: 'As needed',
        why: 'Heavy periods are a common cause of low iron in women. A gynaecologist can check for this if it applies to you.',
      },
    ],
    action: { id: 'find-consultant', label: 'Find a consultant' },
    note: "Your lab's consultant directory is connected during onboarding (demo).",
  },
];

function Row({ row, open, onToggle }) {
  const whyId = `rec-why-${row.slug}`;
  const Art = row.art;
  return (
    <li className={`rec-row${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="rec-row__btn"
        aria-expanded={open}
        aria-controls={whyId}
        onClick={onToggle}
        data-trapezium-id={`${SCREEN}.open-${row.slug}`}
      >
        {Art && (
          <span className={`rec-row__art${row.disc ? ' rec-row__art--disc' : ''}`} aria-hidden="true">
            <Art />
          </span>
        )}
        <span className="rec-row__text">
          <strong>{row.title}</strong>
          <span>{row.sub}</span>
        </span>
        {row.pill && <span className="rec-pill">{row.pill}</span>}
        <ChevronRight className="rec-row__chev" size={16} strokeWidth={2.4} aria-hidden="true" />
      </button>
      <p id={whyId} className="rec-row__why" hidden={!open} data-trapezium-state={open ? 'expanded' : 'collapsed'}>
        {row.why}
      </p>
    </li>
  );
}

function Column({ col, openRows, toggleRow, footerOpen, toggleFooter }) {
  const Icon = col.icon;
  const extraId = `rec-more-${col.key}`;
  return (
    <section className={`rec-col rec-col--${col.key}`} aria-labelledby={`rec-col-${col.key}`}>
      <header className="rec-col__head">
        <span className={`rec-col__icon rec-col__icon--${col.tone}`} aria-hidden="true">
          <Icon size={34} />
        </span>
        <span>
          <h2 id={`rec-col-${col.key}`} className="rec-col__title">
            {col.title}
          </h2>
          <span className="rec-col__sub">{col.sub}</span>
        </span>
      </header>
      <ul className="rec-rows">
        {col.rows.map((row) => (
          <Row key={row.slug} row={row} open={openRows.has(row.slug)} onToggle={() => toggleRow(row.slug)} />
        ))}
      </ul>
      <button
        type="button"
        className={`rec-action rec-action--${col.key}`}
        aria-expanded={footerOpen}
        aria-controls={extraId}
        onClick={toggleFooter}
        data-trapezium-id={`${SCREEN}.${col.action.id}`}
      >
        {col.action.label}
        <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
      </button>
      {col.more ? (
        <div
          id={extraId}
          className="rec-more"
          hidden={!footerOpen}
          data-trapezium-state={footerOpen ? 'expanded' : 'collapsed'}
        >
          <h3 className="rec-more__title">{col.more.title}</h3>
          <ul>
            {col.more.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p
          id={extraId}
          className="rec-more rec-more--note"
          hidden={!footerOpen}
          data-trapezium-state={footerOpen ? 'expanded' : 'collapsed'}
        >
          {col.note}
        </p>
      )}
    </section>
  );
}

/* Severity is shown as a word as well as the mark colour. */
const KEY_FINDINGS = [
  { name: 'Iron-deficiency anaemia', tone: 'high' },
  { name: 'Vitamin D deficiency', tone: 'borderline' },
  { name: 'Raised ESR', tone: 'borderline' },
];

/* Blurred report lines on the side card, as in the photo (decorative). */
const REPORT_LINES = ['#1fa463', '#1fa463', null, '#f2b01f', '#e5484d', '#e5a1b0'];

function ReportSide() {
  const r = DEFAULT_RESULT;
  const tone = TIER_TONE[r.tier];
  const arc = useId();
  return (
    <div className="rec-report-stack">
      <aside className="card rec-report" aria-labelledby="rec-report-title">
        <svg className="rec-report__defs" width="0" height="0" aria-hidden="true" focusable="false">
          <defs>
            <linearGradient id={arc} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ffc53d" />
              <stop offset="1" stopColor="#f5841f" />
            </linearGradient>
          </defs>
        </svg>
        <h2 id="rec-report-title" className="rec-report__title">
          Your report
        </h2>
        <div className="rec-report__score">
          <ScoreRing value={r.score} label="Wellness Score" color={`url(#${arc})`} size={80} className="rec-ring" />
          <p>
            <strong style={{ color: tone.word ?? tone.ink }}>{r.tier}</strong>
            <span>Needs attention</span>
          </p>
        </div>
        <span className="rec-report__lines" aria-hidden="true">
          {REPORT_LINES.map((c, i) => (
            <span key={`line-${i + 1}`} className={c ? undefined : 'is-gap'} style={c ? { '--dot': c } : undefined} />
          ))}
        </span>
        <h3 className="rec-report__sub">Key findings</h3>
        <ul className="rec-report__findings">
          {KEY_FINDINGS.map((f) => (
            <li key={f.name} className={`is-${f.tone}`}>
              <AlertGlyph size={14} color={f.tone === 'high' ? '#e5484d' : '#f2b01f'} />
              <span className="rec-report__finding">{f.name}</span>
              <span className="rec-report__status">{STATUS_LABELS[f.tone]}</span>
            </li>
          ))}
        </ul>
        <Link to="/product/smart-reports" className="rec-report__link" data-trapezium-id={`${SCREEN}.view-full-report`}>
          View full report
          <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
        </Link>
      </aside>
    </div>
  );
}

export default function Recommendations() {
  const [openRows, setOpenRows] = useState(() => new Set());
  const [footers, setFooters] = useState({});

  const toggleRow = (slug) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  const toggleFooter = (key) => setFooters((f) => ({ ...f, [key]: !f[key] }));

  return (
    <Screen id={SCREEN} title="Recommendations" className="rec">
      <ProductChip icon={ListChecks} name="Recommendations" stage="Counsel" tone="counsel" />
      <h1 className="headline pd-headline rec-headline">
        Clear next steps for <br />
        <em>better health.</em>
      </h1>
      <p className="lede pd-lede">Based on your results, here&apos;s what we recommend.</p>

      <div className="rec-main">
        <div className="card rec-board">
          {COLUMNS.map((col) => (
            <Column
              key={col.key}
              col={col}
              openRows={openRows}
              toggleRow={toggleRow}
              footerOpen={Boolean(footers[col.key])}
              toggleFooter={() => toggleFooter(col.key)}
            />
          ))}
        </div>
        <ReportSide />
      </div>
    </Screen>
  );
}
